import React, { useState } from 'react';
import classNames from 'classnames';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import FormattedCurrency from '../../../../../components/currency/FormattedCurrency';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { BudgetConsumptionNode } from '../types';
import { ConsumptionBar } from './ConsumptionBar';
import { ReviewManagerLink } from './ReviewManagerLink';

type Props = {
  node: BudgetConsumptionNode;
  buildDetailUrl: (reviewId: string) => string;
  // Nesting level, drives the indentation of drilled-down rows.
  depth?: number;
};

// One row of the budget consumption tree: a collapsible chevron (when the node
// has reports), the manager name (an access-aware link), a consumed/available
// bar and the consumed / available / remaining figures. Starts collapsed;
// drill-down is on demand.
export const BudgetConsumptionNodeRow: React.FC<Props> = ({
  node,
  buildDetailUrl,
  depth = 0,
}) => {
  const t = useNamespacedTranslation('compensationReviews.budget');
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children.length > 0;

  return (
    <div className="budget-consumption-node">
      <div
        className="budget-consumption-node__row"
        style={{ paddingLeft: `${depth * 1.5}rem` }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            {hasChildren ? (
              <button
                type="button"
                className="budget-consumption-node__toggle"
                aria-expanded={expanded}
                aria-label={expanded ? t('collapse') : t('expand')}
                onClick={() => setExpanded((value) => !value)}>
                <FlairIcon
                  icon={
                    expanded
                      ? 'chevron-down-outline'
                      : 'chevron-forward-outline'
                  }
                  size="sm"
                />
              </button>
            ) : (
              <span className="budget-consumption-node__toggle-placeholder" />
            )}
            <span className="fw-bold">
              <ReviewManagerLink
                reviewId={node.reviewId}
                name={node.name}
                accessible={node.accessible}
                buildDetailUrl={buildDetailUrl}
              />
            </span>
          </div>
          {node.consumedPercent !== null && (
            <span
              className={classNames('small', {
                'text-danger fw-bold': node.overBudget,
                'text-muted': !node.overBudget,
              })}>
              {t('used', { percent: node.consumedPercent })}
            </span>
          )}
        </div>

        {node.hasBudget ? (
          <>
            <ConsumptionBar
              consumedPercent={node.consumedPercent}
              overBudget={node.overBudget}
            />
            <div className="d-flex flex-wrap text-muted small mt-1">
              <span className="me-3">
                {t('consumed')}{' '}
                <FormattedCurrency
                  value={node.consumed}
                  currency={node.currency}
                />
              </span>
              {node.available !== null && (
                <span className="me-3">
                  {t('available')}{' '}
                  <FormattedCurrency
                    value={node.available}
                    currency={node.currency}
                  />
                </span>
              )}
              {node.remaining !== null && (
                <span
                  className={classNames({
                    'text-danger': node.remaining < 0,
                  })}>
                  {t('remaining')}{' '}
                  <FormattedCurrency
                    value={node.remaining}
                    currency={node.currency}
                  />
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="text-muted small mt-1">
            <span className="me-2">
              {t('consumed')}{' '}
              <FormattedCurrency
                value={node.consumed}
                currency={node.currency}
              />
            </span>
            <span>· {t('noBudgetSet')}</span>
          </div>
        )}
      </div>

      {expanded &&
        node.children.map((child) => (
          <BudgetConsumptionNodeRow
            key={child.reviewId}
            node={child}
            buildDetailUrl={buildDetailUrl}
            depth={depth + 1}
          />
        ))}
    </div>
  );
};
